/**
 * Pure calendar arithmetic over `YYYY-MM-DD` (#2875, ADR-0011 decision seven).
 *
 * The input is a calendar day the backend (or `useToday()`) already resolved;
 * the output is another calendar day. No instant and no time zone is involved,
 * so the arithmetic runs on UTC midnight through `Date.UTC` and the UTC getters
 * only — a day there is always 24 hours long, whatever the host zone. It never
 * reads `toISOString()`: cutting an ISO string is how an instant's UTC day
 * leaks into the UI, which `tenant-timezone-rendering.test.ts` forbids.
 */

function parts(day: string): [number, number, number] {
  const [year, month, date] = day.split('-').map(Number);
  return [year, month, date];
}

/** The `YYYY-MM-DD` of an epoch millisecond value, read on the UTC calendar. */
export function calendarDayOfUtcMs(ms: number): string {
  const d = new Date(ms);
  const year = String(d.getUTCFullYear()).padStart(4, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const date = String(d.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${date}`;
}

/**
 * `day` moved by `days` calendar days (negative = back). An overflowing input
 * such as `2026-02-31` normalizes (to `2026-03-03`), so
 * `addCalendarDays(day, 0) === day` is a validity check.
 */
export function addCalendarDays(day: string, days: number): string {
  const [year, month, date] = parts(day);
  return calendarDayOfUtcMs(Date.UTC(year, month - 1, date + days));
}

/** The ISO weekday of `day`: 1 = Monday ... 7 = Sunday (never 0). */
export function isoWeekdayOf(day: string): number {
  const [year, month, date] = parts(day);
  return new Date(Date.UTC(year, month - 1, date)).getUTCDay() || 7;
}

/** The ISO Monday (week start) of the week that contains `day`. */
export function isoMondayOf(day: string): string {
  return addCalendarDays(day, 1 - isoWeekdayOf(day));
}
